import React, { useState, useCallback, useRef, useEffect } from "react";
import { Chat, Button, Row, Col, List, Input, Select, Tag, Divider, RadioGroup, Radio } from "@douyinfe/semi-ui";
import { IconSend, IconSearch, IconComment, IconFaceuLogo, IconGithubLogo } from "@douyinfe/semi-icons";
import { localData } from "./data.js"

interface Message {
  content: string;
  role: "user" | "assistant";
  status: string;
  createAt?: number;
}

const roleInfo = {
  user: {
    name: "User",
    avatar:
      "https://lf3-static.bytednsdoc.com/obj/eden-cn/ptlz_zlp/ljhwZthlaukjlkulzlp/docs-icon.png",
  },
  assistant: {
    name: "Assistant",
    avatar:
      "https://lf3-static.bytednsdoc.com/obj/eden-cn/ptlz_zlp/ljhwZthlaukjlkulzlp/other/logo.png",
  },
  system: {
    name: "System",
    avatar:
      "https://lf3-static.bytednsdoc.com/obj/eden-cn/ptlz_zlp/ljhwZthlaukjlkulzlp/other/logo.png",
  },
};

const commonOuterStyle = {
  border: "1px solid var(--semi-color-border)",
  borderRadius: "16px",
  height: "75vh",
};

const AiChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isGenerating, setIsGenerating] = useState(true);
  const controller = useRef<AbortController>();
  const [scrollRef, setScrollRef] = useState<HTMLElement | null>(null);
  const [currModel, setCurrModel] = useState("qwen-omni-turbo");
  const uploadProps = { action: 'https://api.semi.design/upload' }

  const data = localData;
  const [list, setList] = useState(data);

  useEffect(() => {
    setMessages([{ role: "assistant", status: "", content: "你好! 我是你的AI助手，对我提问请描述清楚场景和角色，比如：你是销售，正在跟进客户，如何说开场白？ " }]);
  }, [])

  const onSearch = (string: any) => {
    let newList;
    if (string) {
      newList = data.filter((item) => item.includes(string));
    } else {
      newList = data;
    }
    setList(newList);
  };

  const models = [
    { value: 'qwen-omni-turbo', label: '通义千问（快速）' },
    { value: 'deepseek-r1', label: 'DeepSeek（强大、专业）' },
  ]

  // 调用 DeepSeek API
  const callDeepSeekAPI = async (input: string) => {
    const DEEPSEEK_API_KEY = "sk-a750fb5b5b8f4a8c9fd8193f95d7dbfd"; // 替换实际API Key
    const API_ENDPOINT =
      "https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions";

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${DEEPSEEK_API_KEY}`,
        },
        body: JSON.stringify({
          model: currModel,
          messages: [{ role: "user", content: input }],
          stream: true, // 启用流式响应
        }),
        signal: controller.current?.signal,
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullResponse = "";

      while (true) {
        const { done, value } = await reader!.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = JSON.parse(line.slice(6));
            if (data.choices[0].delta.content) {
              fullResponse += data.choices[0].delta.content;
              // 更新最后一条消息内容
              setMessages((prev) => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1].content = fullResponse;
                newMessages[newMessages.length - 1].status = "incomplete";
                return newMessages;
              });
            }
          }
          scrollToBottom();
        }
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error("API Error:", error);
      }
    } finally {
      setIsGenerating(false);
      setMessages((prev) => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1].status = "complete";
        return newMessages;
      });
    }
  };

  // 发送消息处理
  const handleSend = async (message: string) => {
    if (!message.trim()) return;

    // 添加用户消息
    setMessages((prev) => [
      ...prev,
      {
        content: message,
        role: "user",
        status: "complete",
        createAt: Date.now(),
      },
      {
        content: "",
        role: "assistant",
        status: "loading",
        createAt: Date.now(),
      }, // 占位助理消息
    ]);

    setIsGenerating(true);
    controller.current = new AbortController();
    await callDeepSeekAPI(message);
  };

  // 停止生成
  const handleStop = () => {
    setMessages((prev) => {
      const newMessages = [...prev];
      newMessages[newMessages.length - 1].status = "complete";
      return newMessages;
    });
    controller.current?.abort();
    setIsGenerating(false);
    scrollToBottom();
  };

  // 消息列表转换
  const chatMessages: any = messages.map((msg) => ({
    role: msg.role,
    status: msg.status,
    content: msg.content,
    createTime: msg.createAt,
  }));

  const scrollToBottom = useCallback(() => {
    if (scrollRef) {
      scrollRef.scrollTop = scrollRef.scrollHeight;
    }
  }, [scrollRef]);

  const modelChange = (model: any) => {
    setCurrModel(model);
  };

  return (
    <>
      <Row gutter={[16, 16]}>

        <Col span={24}>
          <Tag style={{ marginBottom: '15px' }}
            color='light-blue'
            prefixIcon={<IconGithubLogo />}
            size='large'
            shape='circle'
          >
            切换大模型：
          </Tag>
          {/* <Select
            style={{ width: '250px', margin: '10px' }}
            onChange={modelChange}
            defaultValue={models[0].value}
          >
            {models.map(pro => (
              <Select.Option value={pro.value} key={pro.value}>
                {pro.label}
              </Select.Option>
            ))}
          </Select> */}
          <RadioGroup
            onChange={e => modelChange(e.target.value)}
            value={currModel}
            type="button"
            buttonSize="large"
            direction="horizontal"
            style={{
              display: 'flex',
              width: 400,
              justifyContent: 'center',
            }}
          >
            {models.map(pro => (
              <Radio value={pro.value}>{pro.label}</Radio>
            ))}
          </RadioGroup>
        </Col>

      </Row>
      <Divider />
      <Row>
        <Col span={4}>
          {/* <div>
        <div style={{ color: '#E91E63', fontSize:40, margin:10 }} >
            <IconFaceuLogo size="inherit"/><span style={{ color: '#E91E63', fontSize:30, margin:10 }}>AI助手</span>
        </div>
        <br/>
        <div style={{ fontSize:12, margin:10 }}>
            <IconAlarm /><span style={{ marginLeft:5 }}>对AI提问请描述清楚场景和角色，比如："你是销售，正在跟进客户，如何说开场白？"</span>
        </div>
      </div> */}
          <div style={{ height: "75vh", margin: 5, overflowY: "auto" }}>
            <List
              className="component-list-demo-booklist"
              dataSource={list}
              split={false}
              header={
                <Input
                  onCompositionEnd={(v: any) => onSearch(v.target.value)}
                  onChange={(v) => (!v ? onSearch(v) : null)}
                  placeholder="搜索"
                  prefix={<IconSearch />}
                />
              }
              size="small"
              style={{
                flexBasis: "100%",
                flexShrink: 0,
                borderBottom: "1px solid var(--semi-color-border)",
              }}
              renderItem={(item) => (
                <List.Item className="list-item">
                  <Button icon={<IconComment />} type="secondary" onClick={() => handleSend("你在电话跟进贷款客户，" + item)}>
                    {item}
                  </Button>
                </List.Item>
              )}
            />
          </div>
        </Col>
        <Col span={20}>
          <div style={{ height: "84vh", width: "200vh" }}>
            <Chat
              ref={(ref: any) => setScrollRef(ref)}
              uploadProps={uploadProps}
              chats={chatMessages}
              showStopGenerate={true}
              onStopGenerator={handleStop}
              onMessageSend={handleSend}
              style={commonOuterStyle}
              roleConfig={roleInfo}
              placeholder="请输入您的问题..."
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default AiChat;
